import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import { Mail, Clock, CheckCircle, Users, Calendar, Bell, ChevronRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

// EmailJS Configuration - Replace with your actual credentials
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "your_service_id";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "your_template_id";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "your_public_key";

// Google Sheets Web App URL - Replace with your actual deployed script URL
const GOOGLE_SHEETS_URL = process.env.REACT_APP_GOOGLE_SHEETS_URL || "your_google_script_url";

function App() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    business_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [utmParams, setUtmParams] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_term: "",
    utm_content: ""
  });

  // Extract UTM parameters from URL on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = {
      utm_source: searchParams.get('utm_source') || "",
      utm_medium: searchParams.get('utm_medium') || "",
      utm_campaign: searchParams.get('utm_campaign') || "",
      utm_term: searchParams.get('utm_term') || "",
      utm_content: searchParams.get('utm_content') || ""
    };
    
    setUtmParams(params);
    
    // Optional: Log UTM parameters for debugging
    if (Object.values(params).some(val => val !== "")) {
      console.log("UTM Parameters captured:", params);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitAnother = () => {
    setIsSubmitted(false);
    setSubmittedName("");
    // Scroll to form
    document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. Submit to Google Sheets (including UTM parameters)
      const sheetsResponse = await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors', // Important for Google Apps Script
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          ...utmParams, // Include UTM parameters
          timestamp: new Date().toISOString(),
        }),
      });

      // 2. Send email notification via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          service_interest: formData.service_interest,
          business_type: formData.business_type || "Not specified",
          message: formData.message || "No additional comments",
          utm_source: utmParams.utm_source || "Direct",
          utm_medium: utmParams.utm_medium || "N/A",
          utm_campaign: utmParams.utm_campaign || "N/A",
          to_email: "stepnsit@gmail.com", // Replace with your notification email
        },
        EMAILJS_PUBLIC_KEY
      );

      // Save name for thank you message
      setSubmittedName(formData.name);
      
      // Show thank you message
      setIsSubmitted(true);
      
      toast.success("Registration Successful!", {
        description: "Thank you for joining! We'll be in touch soon.",
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_interest: "",
        business_type: "",
        message: ""
      });

    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Registration Failed", {
        description: "Something went wrong. Please try again or contact us directly.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="header">
        <div className="header-content">
          <div className="header-logo" data-testid="header-logo">
            <img 
              src="/logo.png" 
              alt="Step and Sit Logo" 
              className="logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }}>
              <h2>Step and Sit</h2>
              <span className="logo-tagline">No Wait. Just Sit.</span>
            </div>
          </div>
          <div className="header-actions">
            <Button 
              variant="outline"
              className="header-login"
              data-testid="header-login-button"
              onClick={() => window.location.href = 'https://app.stepandsit.com'}
            >
              Login
            </Button>
            <Button 
              className="header-cta"
              data-testid="header-cta-button"
              onClick={() => document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Join Waiting List
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title" data-testid="hero-title">
              No Wait. Just Sit.
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Book lifestyle services instantly. Salons, pet grooming, tattoos, and more – all in one platform.
            </p>
            <div className="hero-cta">
              <Button 
                size="lg" 
                className="cta-button"
                data-testid="get-early-access-btn"
                onClick={() => document.getElementById('register-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Early Access
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-card card-1">
              <Clock className="icon" />
              <span>Real-time Availability</span>
            </div>
            <div className="floating-card card-2">
              <CheckCircle className="icon" />
              <span>Verified Providers</span>
            </div>
            <div className="floating-card card-3">
              <Bell className="icon" />
              <span>Instant Booking</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2 className="section-title" data-testid="features-title">Why Choose Step and Sit?</h2>
          <p className="section-subtitle">Your time, your turn – every time</p>
        </div>
        
        <div className="features-grid">
          <div className="feature-card" data-testid="feature-instant-booking">
            <div className="feature-icon">
              <Calendar />
            </div>
            <h3>Instant Booking</h3>
            <p>Book appointments in seconds. No calls, no waiting – just seamless scheduling at your fingertips.</p>
          </div>

          <div className="feature-card" data-testid="feature-verified-providers">
            <div className="feature-icon">
              <CheckCircle />
            </div>
            <h3>Verified Providers</h3>
            <p>Connect with trusted professionals. Every service provider is verified for quality and reliability.</p>
          </div>

          <div className="feature-card" data-testid="feature-real-time">
            <div className="feature-icon">
              <Clock />
            </div>
            <h3>Real-Time Updates</h3>
            <p>Stay informed with live availability and instant confirmations. Your time is respected.</p>
          </div>

          <div className="feature-card" data-testid="feature-multi-service">
            <div className="feature-icon">
              <Users />
            </div>
            <h3>Multi-Service Platform</h3>
            <p>From salons to pet spas, tattoo parlors to more – everything you need in one place.</p>
          </div>
        </div>
      </section>

      {/* For Users Section */}
      <section className="audience-section for-users">
        <div className="audience-content">
          <div className="audience-text">
            <span className="audience-badge">For Modern Users</span>
            <h2>Take Control of Your Time</h2>
            <p>Are you tired of waiting in line? Fed up with uncertainty about service availability? Step and Sit puts you in control.</p>
            <ul className="benefits-list">
              <li>
                <CheckCircle className="list-icon" />
                <span>Pre-plan appointments with ease</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Discover trusted, nearby providers</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Eliminate waiting and uncertainty</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Manage all bookings in one app</span>
              </li>
            </ul>
          </div>
          <div className="audience-visual user-visual">
            <div className="stat-card">
              <div className="stat-number">0</div>
              <div className="stat-label">Minutes Waiting</div>
            </div>
          </div>
        </div>
      </section>

      {/* For Businesses Section */}
      <section className="audience-section for-business">
        <div className="audience-content reverse">
          <div className="audience-visual business-visual">
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Bookings Enabled</div>
            </div>
          </div>
          <div className="audience-text">
            <span className="audience-badge business">For Business Owners</span>
            <h2>Grow Your Business Effortlessly</h2>
            <p>Manage schedules, reduce no-shows, and increase appointments. Our platform empowers service providers with smart tools.</p>
            <ul className="benefits-list">
              <li>
                <CheckCircle className="list-icon" />
                <span>Streamlined schedule management</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Reduce idle time and no-shows</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Build repeat clientele</span>
              </li>
              <li>
                <CheckCircle className="list-icon" />
                <span>Access insights and analytics</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Registration Section */}
      <section className="register-section" id="register-form">
        <div className="register-container">
          {!isSubmitted ? (
            <>
              <div className="register-header">
                <h2 className="register-title" data-testid="register-title">Join the Waiting List</h2>
                <p className="register-subtitle">Be the first to experience the future of service booking. Sign up for early access.</p>
              </div>

              <form onSubmit={handleSubmit} className="registration-form" data-testid="registration-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="name">Name/Shop Name *</Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="service_interest">Service Interest *</Label>
                <Select
                  value={formData.service_interest}
                  onValueChange={(value) => handleInputChange('service_interest', value)}
                  required
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="service_interest" data-testid="select-service-interest">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salon">Salon Services</SelectItem>
                    <SelectItem value="pet-grooming">Pet Grooming</SelectItem>
                    <SelectItem value="tattoo">Tattoo Parlor</SelectItem>
                    <SelectItem value="spa">Spa & Wellness</SelectItem>
                    <SelectItem value="other">Other Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="business_type">Are you a business owner? (Optional)</Label>
              <Select
                value={formData.business_type}
                onValueChange={(value) => handleInputChange('business_type', value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="business_type" data-testid="select-business-type">
                  <SelectValue placeholder="Select if applicable" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="salon-owner">Salon Owner</SelectItem>
                  <SelectItem value="pet-grooming-owner">Pet Grooming Studio</SelectItem>
                  <SelectItem value="tattoo-artist">Tattoo Artist</SelectItem>
                  <SelectItem value="spa-owner">Spa/Wellness Center</SelectItem>
                  <SelectItem value="other-business">Other Business</SelectItem>
                  <SelectItem value="not-business">I'm a customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="message">Additional Comments (Optional)</Label>
              <Textarea
                id="message"
                data-testid="textarea-message"
                placeholder="Tell us what you're looking for..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            <Button 
              type="submit" 
              size="lg" 
              className="submit-button"
              data-testid="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  Join the Waiting List
                </>
              )}
            </Button>
          </form>
            </>
          ) : (
            <div className="thank-you-message">
              <div className="thank-you-icon">
                <CheckCircle className="success-icon" />
              </div>
              <h2 className="thank-you-title">Thank You{submittedName && `, ${submittedName.split(' ')[0]}`}! 🎉</h2>
              <p className="thank-you-text">
                Your registration has been successfully submitted. We're excited to have you on our waiting list!
              </p>
              <div className="thank-you-details">
                <div className="detail-item">
                  <Mail className="detail-icon" />
                  <p>Check your email for confirmation</p>
                </div>
                <div className="detail-item">
                  <Bell className="detail-icon" />
                  <p>We'll notify you as soon as we launch</p>
                </div>
                <div className="detail-item">
                  <Users className="detail-icon" />
                  <p>You're one step closer to hassle-free bookings</p>
                </div>
              </div>
              <div className="thank-you-actions">
                <Button 
                  onClick={handleSubmitAnother}
                  className="submit-another-button"
                  variant="outline"
                >
                  Submit Another Registration
                </Button>
                <Button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="back-home-button"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img 
              src="/logo-white.png" 
              alt="Step and Sit Logo" 
              className="footer-logo-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
          </div>
          <div className="footer-info">
              <p style={{textAlign: 'center'}}>Step and Sit LLP</p>
              <p style={{textAlign: 'center'}}>Room No.22/831, Elayavoor South, Mundayad, <br /> ChakkaraKkal, Kannur, Kerala, India - 670594</p>
              <p style={{textAlign: 'center'}}>Email: <a href="mailto:stepnsit@gmail.com">stepnsit@gmail.com</a></p>
              <p style={{textAlign: 'center'}}>Phone: <a href="tel:+918848625313">+91 88486 25313</a></p>
          </div>
          <div className="footer-info">
            <p>© 2025 Step and Sit. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <span className="footer-separator">•</span>
              <Link to="/terms-and-conditions">Terms & Conditions</Link>
              <span className="footer-separator">•</span>
              <Link to="/delete-account">Delete Account</Link>
            </div>
            <p>Powered by <a href="https://digitalitup.in" target="_blank" rel="noopener noreferrer">Digital It Up</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;